import { Location } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ShippingAddress } from '@api/model/shippingAddress';
import * as CheckoutActions from '@store/checkout/checkout.actions';
import { selectIsShippingDeliveryDetailsRequired } from '@store/checkout/checkout.selectors';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, take, takeUntil } from 'rxjs';
import { RouterService } from '@core/services/router.service';
import { selectUserProfileAddresses, selectUserProfileDefaultShippingAddressRef } from '@store/user/user.selectors';
import { UntilDestroy } from '@core/classes/untilDestroy.class';
import { TranslateModule } from '@ngx-translate/core';
import { AddressEditComponent } from '../../../../../core/components/address-edit/address-edit.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { LetDirective } from '@ngrx/component';
import { UserActions } from '@store/user/user.actions';
import { SNAME } from '@core/constants/storage';
@Component({
  selector: 'pbb-edit-address-page',
  templateUrl: './edit-address-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LetDirective, MatButtonModule, RouterLink, MatIconModule, AddressEditComponent, TranslateModule],
})
export class EditAddressPageComponent extends UntilDestroy implements OnInit {
  addresses$: Observable<Array<ShippingAddress>> = this.store.select(selectUserProfileAddresses);
  defaultShippingAddressRef$: Observable<string> = this.store.select(selectUserProfileDefaultShippingAddressRef);
  address!: ShippingAddress;
  isMerchantAddress: boolean = false;
  isLoading: boolean = false;
  continueToShipping: boolean = false;
  pendingShippingAddress?: ShippingAddress;
  routeData$: Observable<any> = this.routerService.routeData;
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    private readonly actions$: Actions<any>,
    private location: Location,
    private routerService: RouterService
  ) {
    super();
  }

  ngOnInit(): void {
    const historyState = window.history.state as any;
    const merchantAddress = historyState?.merchantAddress;
    this.continueToShipping = !!historyState?.continueToShipping;

    if (merchantAddress) {
      this.isMerchantAddress = true;
      this.address = {
        ...merchantAddress,
        street_name: `${[merchantAddress?.street_number, merchantAddress?.street_name].join(' ').trim()}`,
        street_number: ' ',
        shipping_address_ref: undefined,
      };
    } else {
      const addressIndex = parseInt(this.route.snapshot.paramMap.get('id')!);
      if (addressIndex || addressIndex === 0) {
        this.addresses$.pipe(take(1)).subscribe((addresses) => {
          this.address = {
            ...addresses[addressIndex],
            street_name: `${[addresses[addressIndex]?.street_number, addresses[addressIndex]?.street_name]
              .join(' ')
              .trim()}`,
            street_number: ' ',
          };
        });
      }
    }

    this.addresses$.pipe(takeUntil(this.destroy$)).subscribe((addresses) => {
      if (!this.isLoading || !this.pendingShippingAddress) {
        return;
      }

      const savedAddress = addresses?.find((address: ShippingAddress) =>
        this.isSameAddress(address, this.pendingShippingAddress as ShippingAddress)
      );

      if (!savedAddress) {
        return;
      }

      this.isLoading = false;

      if (this.isMerchantAddress && this.continueToShipping) {
        this.continueCheckoutFlow(savedAddress);
        this.pendingShippingAddress = undefined;
        return;
      }

      this.pendingShippingAddress = undefined;
      this.location.back();
    });

    this.actions$
      .pipe(
        ofType(UserActions.addShippingAddressFailed, UserActions.updateShippingAddressFailed),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isLoading = false;
        this.pendingShippingAddress = undefined;
      });
  }

  saveShippingAddress(shippingAddress: ShippingAddress) {
    this.isLoading = true;
    this.store.dispatch(UserActions.modifyShippingAddressList());

    shippingAddress.postal_code = shippingAddress.postal_code.toUpperCase();
    this.pendingShippingAddress = shippingAddress;

    if (shippingAddress.shipping_address_ref) {
      this.store.dispatch(UserActions.updateShippingAddressStart({ shippingAddress }));
    } else {
      this.store.dispatch(UserActions.addShippingAddressStart({ shippingAddress }));
    }
  }

  private continueCheckoutFlow(shippingAddress: ShippingAddress): void {
    this.store
      .select(selectIsShippingDeliveryDetailsRequired)
      .pipe(take(1))
      .subscribe((isShippingDeliveryDetailsRequired) => {
        if (isShippingDeliveryDetailsRequired) {
          this.router.navigate(['/checkout/select-shipping-address']);
          this.store.dispatch(
            CheckoutActions.selectShippingAddressAndFetchOptions({
              shippingAddress,
            })
          );
          return;
        }

        this.store.dispatch(
          CheckoutActions.selectShippingAddressWithPostalCode({
            addressRef: shippingAddress.shipping_address_ref || '',
            postalCode: shippingAddress.postal_code || '',
          })
        );
        sessionStorage.setItem(SNAME.SET_DEVICE_VISITED, 'true');
        this.router.navigate(['/checkout/registered/orderReview']);
      });
  }

  private isSameAddress(left: ShippingAddress, right: ShippingAddress): boolean {
    return (
      (left.recipient_name || '') === (right.recipient_name || '') &&
      (left.street_name || '') === (right.street_name || '') &&
      (left.apartment || '') === (right.apartment || '') &&
      (left.city || '') === (right.city || '') &&
      (left.province || '') === (right.province || '') &&
      (left.postal_code || '').toUpperCase() === (right.postal_code || '').toUpperCase()
    );
  }
}
